import React, { useState } from 'react'
import Modal from './Modal'
import { Field, inputClass } from './Field'
import { OneOffTransaction } from '../types'
import { makeId } from '../lib/id'
import { toISODate } from '../lib/dateRules'
import { useSuggestedNameAndCategory } from './SuggestedFields'
import { Combobox } from './Combobox'
import { ShareNameCheckbox } from './ShareNameCheckbox'

interface Props {
  initial?: OneOffTransaction
  defaultDate?: string
  /**
   * Offered when the form was opened from the combined view, where there's no
   * account in scope to put it in. The chosen id comes back with the item.
   */
  accountChoices?: { id: string; name: string }[]
  onSave: (item: OneOffTransaction, addAnother: boolean, accountId?: string) => void
  onDelete?: () => void
  onClose: () => void
}

export default function OneOffForm({ initial, defaultDate, accountChoices, onSave, onDelete, onClose }: Props) {
  const { name, setName, category, setCategory, nameOptions, categoryOptions, share, setShare, shareOnSave } =
    useSuggestedNameAndCategory('oneoff', initial?.name ?? '', initial?.category ?? '')
  const [amount, setAmount] = useState(initial ? Math.abs(initial.amount).toString() : '')
  const [direction, setDirection] = useState<'in' | 'out'>(initial && initial.amount < 0 ? 'out' : 'in')
  const [date, setDate] = useState(initial?.date ?? defaultDate ?? toISODate(new Date()))
  const [notes, setNotes] = useState(initial?.notes ?? '')
  const [accountId, setAccountId] = useState(accountChoices?.[0]?.id)

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null
    const addAnother = submitter?.dataset.action === 'add-another'
    shareOnSave()
    const signedAmount = (Math.abs(parseFloat(amount) || 0)) * (direction === 'out' ? -1 : 1)
    const item: OneOffTransaction = {
      id: initial?.id ?? makeId(),
      kind: 'oneoff',
      name: name.trim() || 'Untitled',
      amount: signedAmount,
      date,
      category: category.trim() || undefined,
      notes: notes || undefined,
    }
    onSave(item, addAnother, accountId)
  }

  return (
    <Modal title={`${initial ? 'Edit' : 'Add'} one-off transaction`} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <Combobox
          label="Name"
          value={name}
          onChange={setName}
          options={nameOptions}
          required
          autoFocus
          placeholder="e.g. Car repair, Birthday present"
        />

        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount">
            <input className={inputClass} type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} required />
          </Field>
          <Field label="Direction">
            <select className={inputClass} value={direction} onChange={(e) => setDirection(e.target.value as 'in' | 'out')}>
              <option value="out">Money out</option>
              <option value="in">Money in</option>
            </select>
          </Field>
        </div>

        {accountChoices && (
          <Field label="Account">
            <select className={inputClass} value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              {accountChoices.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label="Date">
          <input className={inputClass} type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </Field>

        <Combobox
          label="Category (optional)"
          value={category}
          onChange={setCategory}
          options={categoryOptions}
          placeholder="e.g. Transport, Gifts"
        />

        <Field label="Notes (optional)">
          <input className={inputClass} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>

        <ShareNameCheckbox checked={share} onChange={setShare} />

        <div className="flex items-center justify-between pt-3 border-t border-line mt-2">
          <div>
            {onDelete && (
              <button type="button" onClick={onDelete} className="text-sm text-rag-bad hover:underline">
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-sm border border-line text-ink/80 hover:bg-paper">
              Cancel
            </button>
            {!initial && (
              <button
                type="submit"
                data-action="add-another"
                className="px-3 py-1.5 text-sm rounded-sm border border-accent text-accent hover:bg-accent/10"
              >
                Save &amp; add another
              </button>
            )}
            <button type="submit" data-action="save" className="px-3 py-1.5 text-sm rounded-sm bg-accent text-white hover:bg-accent/90">
              Save
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
